import { useCallback, useMemo } from "react";
import { Feature, MultiPolygon } from "geojson";

import { useGetVersionQuery } from "fond/api";
import { LayerGroupIds, LayerId, LayerIds } from "fond/layers";
import { selectDrawExistingOption, selectDrawNewOption, selectUploadOption } from "fond/project/polygon/redux";
import { closePolygonSelect, getCurrentProjectData, hasLayer, polygonSelect, setActiveLayerGroupId } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

export type AreaSelect = {
  layerIds: LayerId[];
  /** Whether the layer group already has data that area select would replace. */
  hasData: boolean;
  /** The version's input data area, which can be reused as the selected area. */
  uploadedArea: Feature<MultiPolygon> | null;
  beginPolygonSelect(): void;
  selectExistingPolygon(): void;
  selectNewPolygon(): void;
  selectPolygonUpload(): void;
  close(): void;
};

/** Return the state and actions for area selecting the features of a layer group. */
export const useAreaSelect = (layerGroupId: string): AreaSelect => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const data = useAppSelector((state) => getCurrentProjectData(state.project));
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });

  const layerIds = useMemo(() => {
    if (layerGroupId === LayerGroupIds.inStreet || layerGroupId === LayerGroupIds.inBaseDataStreet) {
      return [LayerIds.inStreet];
    } else if (layerGroupId === LayerGroupIds.spanPole) {
      return [LayerIds.inSpan, LayerIds.inPole];
    } else if (layerGroupId === LayerGroupIds.inParcel) {
      return [LayerIds.inParcel];
    }

    return [LayerIds.inAddress];
  }, [layerGroupId]);

  const hasData = useMemo(
    () =>
      layerGroupId === LayerGroupIds.inBaseDataStreet
        ? hasLayer(data, LayerIds.inBaseDataStreet)
        : layerIds.some((layerId) => hasLayer(data, layerId)),
    [data, layerGroupId, layerIds]
  );

  const beginPolygonSelect = useCallback(() => {
    // inAddress is the layer ID in the auto design panel, but inParcel is what
    // the polygon select logic expects.
    const payload = { layerIds: layerIds.map((id) => (id === LayerIds.inAddress ? LayerIds.inParcel : id)) };
    // Polygon select reads the active layer group to decide which layers to import.
    dispatch(setActiveLayerGroupId(layerGroupId));
    dispatch(polygonSelect(payload));
  }, [dispatch, layerGroupId, layerIds]);

  const selectExistingPolygon = useCallback(() => dispatch(selectDrawExistingOption(version?.UploadedArea)), [dispatch, version?.UploadedArea]);
  const selectNewPolygon = useCallback(() => dispatch(selectDrawNewOption()), [dispatch]);
  const selectPolygonUpload = useCallback(() => dispatch(selectUploadOption()), [dispatch]);
  const close = useCallback(() => dispatch(closePolygonSelect()), [dispatch]);

  return {
    layerIds,
    hasData,
    uploadedArea: version?.UploadedArea ?? null,
    beginPolygonSelect,
    selectExistingPolygon,
    selectNewPolygon,
    selectPolygonUpload,
    close,
  };
};
